// PlaceMiniMap - Mini-Karte fuer PlaceDetail mit Route-Button

import React, { useEffect, useRef, useState } from 'react';
import { Navigation, MapPin, Loader2, AlertCircle } from 'lucide-react';
import { formatDistance, estimateWalkingTime, calculateDistance } from '../../utils/distance';

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface PlaceMiniMapProps {
  latitude: number;
  longitude: number;
  name: string;
  address?: string;
  city?: string;
  userLocation?: Coordinates | null;
}

// Custom map styles (same as MapView)
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [{ color: '#c8e6c9' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [{ color: '#b3e5fc' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#e0e0e0' }],
  },
];

export function PlaceMiniMap({
  latitude,
  longitude,
  name,
  address,
  city,
  userLocation,
}: PlaceMiniMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate distance info
  const distanceInfo = userLocation
    ? {
        distanceKm: calculateDistance(userLocation, { latitude, longitude }),
        distanceText: formatDistance(userLocation, { latitude, longitude }),
        walkingTime: estimateWalkingTime(
          calculateDistance(userLocation, { latitude, longitude })
        ),
      }
    : null;

  // Initialize map
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey || apiKey === 'YOUR_API_KEY_HERE') {
      // Kein API Key - zeige trotzdem die Karte-Alternative ohne Fehler
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const { setOptions, importLibrary } = await import('@googlemaps/js-api-loader');

        setOptions({
          key: apiKey,
          v: 'weekly',
          libraries: ['maps', 'marker'],
          language: 'de',
          region: 'DE',
        });

        await importLibrary('maps');

        if (!isMounted || !mapRef.current) return;

        const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || undefined;

        const map = new google.maps.Map(mapRef.current, {
          center: { lat: latitude, lng: longitude },
          zoom: 15,
          styles: MAP_STYLES,
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'cooperative',
          ...(mapId && { mapId }),
        });

        mapInstanceRef.current = map;

        // Versuche AdvancedMarker, fallback auf normalen Marker
        let useAdvancedMarkers = false;
        try {
          await importLibrary('marker');
          // Teste ob AdvancedMarkerElement verfuegbar ist
          if (google.maps.marker?.AdvancedMarkerElement && mapId) {
            useAdvancedMarkers = true;
          }
        } catch {
          // marker library nicht verfuegbar - nutze normale Marker
        }

        if (useAdvancedMarkers) {
          // Place marker (Advanced)
          const placeMarkerElement = document.createElement('div');
          placeMarkerElement.innerHTML = `
            <div class="w-10 h-10 bg-indigo-500 rounded-full shadow-lg flex items-center justify-center border-2 border-white">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
          `;

          new google.maps.marker.AdvancedMarkerElement({
            map,
            position: { lat: latitude, lng: longitude },
            content: placeMarkerElement,
            title: name,
          });

          // User location marker (if available)
          if (userLocation) {
            const userMarkerElement = document.createElement('div');
            userMarkerElement.innerHTML = `
              <div class="relative">
                <div class="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg"></div>
                <div class="absolute inset-0 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-75"></div>
              </div>
            `;

            new google.maps.marker.AdvancedMarkerElement({
              map,
              position: { lat: userLocation.latitude, lng: userLocation.longitude },
              content: userMarkerElement,
              title: 'Dein Standort',
            });
          }
        } else {
          // Fallback: Normale Marker (ohne Map ID)
          new google.maps.Marker({
            map,
            position: { lat: latitude, lng: longitude },
            title: name,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: '#6366f1',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            },
          });

          // User location marker (if available)
          if (userLocation) {
            new google.maps.Marker({
              map,
              position: { lat: userLocation.latitude, lng: userLocation.longitude },
              title: 'Dein Standort',
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#3b82f6',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
              },
            });
          }
        }

        // Fit bounds to show both markers if user location available
        if (userLocation) {
          const bounds = new google.maps.LatLngBounds();
          bounds.extend({ lat: latitude, lng: longitude });
          bounds.extend({ lat: userLocation.latitude, lng: userLocation.longitude });
          map.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
        }

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to load Google Maps:', err);
        if (isMounted) {
          // Zeige keinen Fehler, sondern lade einfach ohne Karte
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [latitude, longitude, name, userLocation]);

  // Open Google Maps for navigation
  const handleOpenMaps = () => {
    // Use coordinates if address not available
    const destination = address
      ? encodeURIComponent(`${address}${city ? `, ${city}` : ''}`)
      : `${latitude},${longitude}`;

    // If user location available, open with directions
    if (userLocation) {
      const url = `https://www.google.com/maps/dir/?api=1&origin=${userLocation.latitude},${userLocation.longitude}&destination=${destination}&travelmode=walking`;
      window.open(url, '_blank');
    } else {
      // Just open location
      const url = address
        ? `https://www.google.com/maps/search/?api=1&query=${destination}`
        : `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(url, '_blank');
    }
  };

  // Zeige statische Karten-Alternative wenn kein API Key oder Fehler
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const showStaticFallback = !apiKey || apiKey === 'YOUR_API_KEY_HERE' || error;

  if (showStaticFallback && !isLoading) {
    return (
      <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Static Map Image Fallback */}
        <div
          className="h-[180px] bg-gradient-to-br from-indigo-50 to-blue-50 flex flex-col items-center justify-center cursor-pointer hover:from-indigo-100 hover:to-blue-100 transition"
          onClick={handleOpenMaps}
        >
          <MapPin className="w-12 h-12 text-indigo-500 mb-2" />
          <p className="text-sm text-gray-600 font-medium">Standort anzeigen</p>
          <p className="text-xs text-gray-400 mt-1">Tippen um Karte zu oeffnen</p>
        </div>
        <div className="p-3 bg-white border-t border-gray-100">
          {/* Distance Info */}
          {distanceInfo && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <MapPin className="w-4 h-4 text-indigo-500" />
              <span className="font-medium text-gray-900">{distanceInfo.distanceText}</span>
              <span className="text-gray-400">·</span>
              <span>{distanceInfo.walkingTime}</span>
            </div>
          )}
          <button
            onClick={handleOpenMaps}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
          >
            <Navigation className="w-4 h-4" />
            <span>Route planen</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Map */}
      <div className="relative h-[180px]">
        <div ref={mapRef} className="w-full h-full" />

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
          </div>
        )}
      </div>

      {/* Info + Route Button */}
      <div className="p-3 bg-white border-t border-gray-100">
        {/* Distance Info */}
        {distanceInfo && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <MapPin className="w-4 h-4 text-indigo-500" />
            <span className="font-medium text-gray-900">{distanceInfo.distanceText}</span>
            <span className="text-gray-400">·</span>
            <span>{distanceInfo.walkingTime}</span>
          </div>
        )}

        {/* Route Button */}
        <button
          onClick={handleOpenMaps}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
        >
          <Navigation className="w-4 h-4" />
          <span>Route planen</span>
        </button>
      </div>
    </div>
  );
}

export default PlaceMiniMap;
